import React, { Suspense, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOLabel from "@atlaskit/react-ufo/label";
import { token } from "@atlaskit/tokens";

import {
    LanguageGlobeIcon,
    languages
} from "@/components/global-footer/language-selector/src/common";
import { LinkWithIcon } from "@/components/global-nav/list-of-links/src/link-with-icon";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    width: "100%",
    maxWidth: "1216px",
    margin: "auto",
    paddingBlock: "24px",
    paddingInline: token("space.200"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.400"),

    [media.above.sm]: {
        paddingInline: "44px",
        gap: token("space.400")
    },

    [media.above.md]: {
        paddingInline: "24px",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    }
});

const linkStyle = css({
    "fontSize": "14px",
    "lineHeight": "14px",
    "height": "14px",
    "textDecoration": "none",
    "color": wacToken("color.primary.black"),

    ":hover": {
        textDecoration: "underline",
        textShadow: "none"
    },

    ":visited, :focus": {
        textShadow: "none"
    }
});

const linkDarkThemeStyle = css({
    color: "var(--footer-link-color)"
});

const copyrightStyle = css({
    color: wacToken("color.neutral.light800"),
    lineHeight: "18px",
    height: "18px",
    fontSize: "14px",

    [media.above.md]: {
        textAlign: "center"
    }
});

const copyrightDarkThemeStyle = css({
    color: wacToken("color.neutral.dark800")
});

const linksAndLanguageStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),

    [media.above.sm]: {
        flexDirection: "row",
        gap: token("space.400"),
        alignItems: "center"
    }
});

const listStyle = css({
    "listStyleType": "none",
    "padding": 0,
    "margin": 0,
    "display": "flex",
    "flexDirection": "column",
    "gap": token("space.200"),

    [media.above.sm]: {
        flexDirection: "row",
        gap: token("space.400"),
        alignItems: "center"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& a:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const listDarkThemeStyle = css({
    "--footer-link-color": wacToken("color.neutral.light0"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a, a span": {
        color: "var(--footer-link-color)"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a:hover, a:hover span": {
        "--footer-link-color": wacToken("color.neutral.dark1100"),
        "color": "var(--footer-link-color)"
    }
} as Record<string, unknown>);

// ==================================================================
// Type Props
// ==================================================================
export interface GlobalFooterBottomProps {
    selectedLanguage?: string;
    onLanguageChangeHandler?: React.Dispatch<string>;
    className?: string;
}

// ==================================================================
// Rendering Functions
// ==================================================================
const BottomLinks = () => {
    const { isDarkTheme } = useDarkTheme();
    const intl = useIntl();

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window?.AtlOneTrustWrapper?.updatePolicyLinks) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.AtlOneTrustWrapper.updatePolicyLinks();
        }
    }, []);

    const bottomLinks = [
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.privacy-policy",
                defaultMessage: "Privacy policy"
            }),
            href: useLocalizedHref("/legal/privacy-policy"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalFooterv2.bottom.notice-at-collection",
                defaultMessage: "Notice at Collection"
            }),
            // eslint-disable-next-line local/ensure-wac-token-usage
            href: "/legal/privacy-policy#additional-disclosures-for-ca-residents",
            isOneTrustWrapped: true
        },
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.terms",
                defaultMessage: "Terms"
            }),
            href: useLocalizedHref("/legal/cloud-terms-of-service"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalfooterv2.bottom.impressum",
                defaultMessage: "Impressum"
            }),
            href: useLocalizedHref("/legal/impressum"),
            isOneTrustWrapped: false
        }
    ];

    return bottomLinks.map((bottomLink) => {
        if (bottomLink.isOneTrustWrapped) {
            return (
                <li
                    key={bottomLink.text}
                    className={"link-wrapper atl-policy-link-container"}
                >
                    <LinkWithIcon
                        linkText={bottomLink.text}
                        href={bottomLink.href}
                        type="anchor"
                        isHaveIcon={false}
                        isUsePlainSpanTagForLinkText={true}
                        className={"atl-policy-link"}
                        spanLinkTextClassName={"atl-policy-link-text"}
                        css={linkStyle}
                    />
                </li>
            );
        } else {
            return (
                <li key={bottomLink.text}>
                    {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                    <Link
                        text={bottomLink.text}
                        href={bottomLink.href}
                        type="anchor"
                        bgColor={isDarkTheme ? "bgBlackBlue" : "bgColored"}
                        css={[linkStyle, isDarkTheme && linkDarkThemeStyle]}
                    />
                </li>
            );
        }
    });
};

const copyRightYear = new Date().getFullYear();

const containerStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#42526e",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center"
});

const containerDarkThemeStyles = css({
    color: wacToken("color.neutral.light0")
});

const labelStyles = css({
    marginInlineStart: token("space.050"),
    fontSize: "0.875rem"
});

const arrowStyles = css({
    marginInlineStart: token("space.050"),
    fontSize: "1.5rem"
});

const SelectorFallback = ({
    selectedLanguage
}: {
    selectedLanguage?: string;
}) => {
    const { isDarkTheme } = useDarkTheme();
    const intl = useIntl();

    if (!selectedLanguage) {
        selectedLanguage = intl.locale || "en";
    }
    const languageLabel = languages.find(
        (lang) => lang.value === selectedLanguage
    )?.label;

    const globeColor = isDarkTheme
        ? wacToken("color.neutral.light0")
        : "#42526e";

    return (
        <div css={[containerStyles, isDarkTheme && containerDarkThemeStyles]}>
            <LanguageGlobeIcon color={globeColor} />
            <span css={labelStyles}>{languageLabel}</span>
            <span css={arrowStyles} aria-hidden="true">
                ▾
            </span>
        </div>
    );
};

const LanguageSelectorLazy = React.lazy(() =>
    import(
        "../../../components/global-footer/language-selector/src/language-selector.tsx"
    )
        .then(({ LanguageSelector }) => ({
            default: LanguageSelector
        }))
        .catch((error) => {
            console.error(error);
            return {
                default: SelectorFallback
            };
        })
);

export const GlobalFooterBottom = ({
    selectedLanguage,
    onLanguageChangeHandler,
    className
}: GlobalFooterBottomProps) => {
    const { isDarkTheme } = useDarkTheme();
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    return (
        <UFOLabel name="global-footer-v2-bottom">
            <div className={className} css={containerStyle}>
                <div
                    css={[
                        copyrightStyle,
                        isDarkTheme && copyrightDarkThemeStyle
                    ]}
                    data-testid="footer-copyright"
                >
                    Copyright © {copyRightYear} Atlassian
                </div>
                <div css={linksAndLanguageStyle}>
                    <UFOLabel name="global-footer-v2-bottom-links">
                        <ul
                            css={[listStyle, isDarkTheme && listDarkThemeStyle]}
                        >
                            <BottomLinks />
                        </ul>
                    </UFOLabel>
                    {isClient ? (
                        <Suspense
                            fallback={
                                <SelectorFallback
                                    selectedLanguage={selectedLanguage}
                                />
                            }
                        >
                            <LanguageSelectorLazy
                                selectedLanguage={selectedLanguage}
                                onLanguageChangeHandler={
                                    onLanguageChangeHandler
                                }
                            />
                        </Suspense>
                    ) : (
                        <SelectorFallback selectedLanguage={selectedLanguage} />
                    )}
                </div>
            </div>
        </UFOLabel>
    );
};
